import React from 'react';
import env from '@lib/envs/env';
import Compose from '@components/shared/Compose';
import ThemeProvider from '@components/theme/ThemeProvider';
import { GetStaticPathsResult, GetStaticProps } from 'next';
import logger from '@lib/logger';
import generatePrismicStaticPathsByDocType from '@lib/prismic/generatePrismicStaticPathsByDocType';
import { IPrismicText } from '@lib/prismic/types';
import fetchLocationDocs, { ILocationDocData } from '@lib/prismic/fetchLocationDocs';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import { ISteps } from '@components/shared/HowDoesItWork/HowDoesItWork';
import Team from '@components/pages/InteriorDesignersLocationPages/Team/Team';
import AVAILABLE_DESIGNERS_QUERY, {
  AvailableDesignersResponse, AvailableDesignersVariables, Designer
} from '@graphql/Designer/queries/availableDesigners.graphql';
import { getApollo } from '@lib/apollo/apolloClient';
import LoginRegisterModalProvider from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import { getSlug } from '@lib/router/router';
import { ILocationPagesFAQ } from '@components/pages/InteriorDesignersLocationPages/shared/model/PrismicData';
import { staticNotFound } from '@utils/navigation/serverSideHelpers';

const REVALIDATE_INTERVAL_SECONDS = env.STATIC_PAGE_DEFAULT_CACHE_SECONDS;

/* eslint-disable camelcase */
export interface IPrismicData {
  team_faq_title: IPrismicText[];
  team_faq_items: ILocationPagesFAQ[];
  team_list: ISteps[];
}

interface ITeamPageProps extends ILocationDocData<IPrismicData> {
  designers: Designer[];
}

export default function TeamPage({
  prismicData: { team_faq_items },
  market,
  cities,
  designers,
}: ITeamPageProps) {
  return (
    <Compose
      components={[
        ThemeProvider,
        LoginRegisterModalProvider
      ]}
    >
      <DefaultTemplate
        navProps={{
          cities,
          hasSubNav: true
        }}
        {...getMetaData(market)}
      >
        <Team
          faqItems={team_faq_items}
          market={market}
          designers={designers}
        />
      </DefaultTemplate>
    </Compose>
  );
}

const PRISMIC_TYPE = 'location_page';

export const getStaticProps: GetStaticProps = async (context) => {
  const log = logger(`Location Pages - Team`);
  const slug = getSlug(context, log);

  const locationData = await fetchLocationDocs<IPrismicData>(slug, context, log);

  if (!locationData) {
    return staticNotFound();
  }

  let designers: Designer[];
  try {
    designers = await fetchDesignersForLocation(locationData.market);
  } catch (error: any) {
    log.error({ context, error }, `Error fetching Designers for location slug ${slug}`);
    return staticNotFound();
  }

  return {
    ...locationData.staticProps,
    props: {
      ...locationData,
      designers
    },
    revalidate: REVALIDATE_INTERVAL_SECONDS
  };
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return generatePrismicStaticPathsByDocType(PRISMIC_TYPE);
}

async function fetchDesignersForLocation(location: string): Promise<Designer[]> {
  const apolloClient = getApollo();

  let designers: Designer[] = [];

  const variables: AvailableDesignersVariables = {
    pageNumber: 1,
    pageSize: 12,
    orderByLocation: {
      location
    }
  };

  const designersResponse = await apolloClient
    .query<AvailableDesignersResponse, AvailableDesignersVariables>({
      query: AVAILABLE_DESIGNERS_QUERY,
      variables,
    });

  designers = designersResponse?.data?.availableDesigners?.items || [];
  return designers;
}

function getMetaData(market: string) {
  return {
    title: `Interior Designers Near You: Havenly's Top Interior Designers in ${market}`,
    description: `Work with Havenly's team of top interior designers bring your dream home to life, tailored to your style, space, and budget`
  };
}
